import { ArrowUpRight } from "lucide-react";
import type React from "react";
import { RiseWords } from "@/components/motion/motion";
import { Container, KeyValues, RunningHead } from "@/components/paper/paper";
import { PrismScene } from "@/components/scenes/PrismScene";
import { Button } from "@/components/ui/button";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	CONTACT_EMAIL,
	GITHUB_URL,
	LINKEDIN_URL,
	SCHEDULE_VISIO_URL,
} from "@/lib/constants";
import { useI18n } from "@/lib/i18n/use-i18n";
import { InternalLink } from "../sections/Sections";

/** First-paint choreography (seconds): name → headline → highlighter → body → contacts. */
const HERO = {
	nameStep: 0.06,
	name: 0.15,
	title: 0.85,
	mark: 1.25,
	intro: 1.15,
	contacts: 1.5,
} as const;

/**
 * The name struck glyph by glyph by a dot-matrix print head. CSS only, so it plays
 * before hydration; the head steps one glyph per strike, then parks and fades.
 */
function DotMatrixName({
	text,
	className,
}: {
	text: string;
	className?: string;
}) {
	const glyphs = [...text];
	const travel = (glyphs.length + 1) * HERO.nameStep;
	return (
		<p className={className} aria-hidden="true">
			<span
				className="dm-print"
				style={
					{
						"--d": `${HERO.name}s`,
						"--step": `${HERO.nameStep}s`,
					} as React.CSSProperties
				}
			>
				{glyphs.map((glyph, i) =>
					glyph === " " ? (
						" "
					) : (
						<span
							key={i}
							className="dm-glyph"
							style={{ "--i": i } as React.CSSProperties}
						>
							{glyph}
						</span>
					),
				)}
				<span className="pointer-events-none absolute inset-0 hidden overflow-clip sm:block">
					<span
						className="dm-carriage"
						style={{
							animation: `dm-carriage ${travel}s steps(${glyphs.length + 1}, jump-none) ${HERO.name}s both, fade-out 0.4s ease ${HERO.name + travel + 0.1}s both`,
						}}
					/>
				</span>
			</span>
		</p>
	);
}

export function HomeHeroSection() {
	const { t, dict } = useI18n();
	const { capture } = useAnalytics();
	const c = dict.home.contacts;
	const proofs = dict.home.hero.proofs;
	const titleLead = t((t) => t.home.hero.titleLead);

	return (
		<section className="pt-8 md:pt-12">
			<Container>
				<RunningHead left="~/simon_boisset" />

				<div className="mt-10 grid gap-12 md:mt-14 lg:grid-cols-[1fr_17.5rem] lg:gap-16">
					<div>
						<DotMatrixName
							text={t((t) => t.nav.brand)}
							className="font-dots text-[clamp(2.6rem,7.6vw,5.6rem)] leading-[0.92] font-extrabold tracking-[-0.02em] text-ink lg:whitespace-nowrap"
						/>
						<h1 className="mt-6 font-mono text-[clamp(1.25rem,2.7vw,1.95rem)] leading-snug font-medium tracking-[-0.01em] text-ink">
							<span className="sr-only">{t((t) => t.nav.brand)}, </span>
							<RiseWords text={titleLead} delay={HERO.title} />{" "}
							<span className="rise-mask -mr-[1ch] pr-[1ch]">
								<span
									className="rise-word whitespace-nowrap"
									style={
										{
											"--d": `${HERO.title}s`,
											"--i": titleLead.split(" ").length,
											"--step": "0.055s",
										} as React.CSSProperties
									}
								>
									<span
										className="pastel-mark mark-sweep"
										style={{ "--d": `${HERO.mark}s` } as React.CSSProperties}
									>
										{t((t) => t.home.hero.titleMark)}
									</span>
									<span
										className="line-rise text-cursor inline-block w-0"
										style={{ animationDelay: `${HERO.mark + 0.5}s` }}
										aria-hidden="true"
									/>
								</span>
							</span>
						</h1>
						<p
							className="line-rise mt-6 max-w-[42rem] text-[1.06rem] leading-[1.7] text-ink-2"
							style={{ animationDelay: `${HERO.intro}s` }}
						>
							{t((t) => t.home.hero.intro)}
						</p>
						<div
							className="line-rise mt-8 flex flex-wrap gap-3"
							style={{ animationDelay: `${HERO.intro + 0.12}s` }}
						>
							<Button asChild size="lg">
								<a
									href={SCHEDULE_VISIO_URL}
									target="_blank"
									rel="noreferrer"
									onClick={() =>
										capture(ANALYTICS_EVENTS.ctaClick, {
											cta: "schedule_call",
											placement: "hero",
											href: SCHEDULE_VISIO_URL,
										})
									}
								>
									{t((t) => t.home.hero.ctaPrimary)}
									<ArrowUpRight aria-hidden="true" />
								</a>
							</Button>
							<InternalLink to="/{-$locale}/about">
								{t((t) => t.home.hero.ctaSecondary)}
							</InternalLink>
						</div>
						<ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[0.8rem] text-ink-2">
							{[proofs.since, proofs.platforms, proofs.stack].map(
								(proof, i) => (
									<li
										key={proof}
										className="line-rise flex items-center gap-2"
										style={{ animationDelay: `${HERO.intro + 0.3 + i * 0.1}s` }}
									>
										<span className="pastel-bar h-2 w-2" aria-hidden="true" />
										{proof}
									</li>
								),
							)}
						</ul>
					</div>

					<KeyValues
						boot={HERO.contacts}
						className="self-end border-l border-dashed border-rule-2 pl-5"
						items={[
							{
								key: c.email,
								value: CONTACT_EMAIL,
								href: `mailto:${CONTACT_EMAIL}`,
							},
							{ key: c.site, value: "simonboisset.com" },
							{
								key: c.linkedin,
								value: "in/simon-boisset",
								href: LINKEDIN_URL,
								external: true,
							},
							{
								key: c.github,
								value: "simonboisset",
								href: GITHUB_URL,
								external: true,
							},
							{ key: c.location, value: c.locationValue },
						]}
					/>
				</div>

				<PrismScene className="mt-14 md:mt-20" />
			</Container>
		</section>
	);
}
